import {
  memo,
  type ReactNode,
  type Ref,
  useCallback,
  useMemo,
  useRef,
} from 'react'

import {
  AutocompleteBase,
  type AutocompleteProps,
  type ComboboxHandle,
} from '@fv/client-components/combobox'

import {
  boldSearchText,
  defaultGetOptionLabel,
  type ShipperClassNames,
  type ShipperInternalKeys,
  shipperDropdownClassNames,
  shipperInputClassNames,
} from './ShipperComboBox'

type Props<T> = Omit<AutocompleteProps<T>, ShipperInternalKeys> & {
  ref?: Ref<ComboboxHandle>
  error?: string
  classNames?: ShipperClassNames<T>
  renderOption?: (option: T) => ReactNode
}

function ShipperAutocompleteImpl<T>({
  ref,
  error,
  classNames,
  getOptionLabel = defaultGetOptionLabel,
  onValueChange,
  renderOption,
  name,
  value,
  ...rest
}: Props<T>) {
  const searchTextRef = useRef('')

  const onValueChangeRef = useRef(onValueChange)
  onValueChangeRef.current = onValueChange

  const handleValueChange = useCallback((val: string) => {
    searchTextRef.current = val
    onValueChangeRef.current(val)
  }, [])

  const defaultRenderOption = useCallback(
    (option: T) =>
      boldSearchText(getOptionLabel(option), searchTextRef.current),
    [getOptionLabel],
  )

  const mergedClassNames = useMemo(
    () => ({
      ...shipperDropdownClassNames,
      ...shipperInputClassNames,
      ...classNames,
    }),
    [classNames],
  )

  return (
    <div className={classNames?.container} data-cy={name}>
      <AutocompleteBase<T>
        ref={ref}
        name={name}
        value={value}
        onValueChange={handleValueChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption ?? defaultRenderOption}
        portalToRoot
        classNames={mergedClassNames}
        {...rest}
      />
    </div>
  )
}

export const ShipperAutocomplete = memo(
  ShipperAutocompleteImpl,
) as typeof ShipperAutocompleteImpl
